import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  AdornedInput,
  Anchor,
  Box,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  Modal,
  Paragraph,
  Text,
} from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconWarningFilled } from '@zillow/constellation-icons';
import { ExpandableDisclaimer, ZillowInsuranceServicesLogo } from '@zillow/zrs-comet';

import ModalFooter from '../ModalFooter';

import SpinnerContainer from '../../SpinnerContainer';
import PropertiesActions from '../../../actions/PropertiesActions';
import { useForm } from '../../../hooks';
import { postingConstraints } from '../../../utils/formValidation';
import { RENTAL_PROTECTION_PAYMENT_OPTIONS_REQUEST_VALUE } from '../../../constants/RentGuaranteeConstants';
import numberUtils from '../../../utils/numberUtils';

const propTypes = {
  close: PropTypes.func.isRequired,
  handleSecurityDepositUpdate: PropTypes.func.isRequired,
  securityDeposit: PropTypes.string.isRequired,
  listingAlias: PropTypes.string.isRequired,
  rentAmount: PropTypes.string,
  selectedOption: PropTypes.string,
};
const defaultProps = {};
const securityDepositInputName = 'securityDeposit';
const rentAmountInputName = 'price';

const SecurityDepositUpdateOnRentGuaranteeDropModal = ({
  close,
  securityDeposit,
  rentAmount,
  selectedOption,
  handleSecurityDepositUpdate,
  handleDropRentalProtectionSecurityDepositCancel,
  handleDropRentalProtectionSecurityDepositExit,
  listingAlias,
}) => {
  const dispatch = useDispatch();
  const [legalDisclaimer, setLegalDisclaimer] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    dispatch(PropertiesActions.getDisclaimers(listingAlias, 'generalV2'))
      .then((response) => {
        setLegalDisclaimer(response);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isLoading]);

  const formattedSecurityDeposit = numberUtils.asCurrency(parseInt(numberUtils.removeDollarSign(securityDeposit)), '0');
  const { inputs, errorObj, hasFieldError, handleChange } = useForm(
    selectedOption
      ? {
          [rentAmountInputName]: rentAmount,
          securityDeposit: formattedSecurityDeposit,
        }
      : { securityDeposit: formattedSecurityDeposit },
    postingConstraints,
  );

  const handleSubmit = () => {
    if (selectedOption !== RENTAL_PROTECTION_PAYMENT_OPTIONS_REQUEST_VALUE.LANDLORD_PAYS) {
      if (
        hasFieldError(securityDepositInputName, inputs.securityDeposit) ||
        (selectedOption && hasFieldError(rentAmountInputName, inputs[rentAmountInputName]))
      ) {
        return;
      }

      handleSecurityDepositUpdate(inputs.securityDeposit, inputs[rentAmountInputName], selectedOption);
    }

    close();
  };

  const securityDepositField = (
    <FormField
      description={<FormHelp error={!!errorObj.securityDeposit}>{errorObj.securityDeposit}</FormHelp>}
      label={<Label>Security Deposit</Label>}
      control={
        <AdornedInput
          startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
          error={!!errorObj.securityDeposit}
          input={
            <Input
              aria-label="Security deposit"
              value={inputs.securityDeposit}
              name={securityDepositInputName}
              placeHolder={inputs.securityDeposit}
              onChange={handleChange}
            />
          }
          endAdornment={
            errorObj.securityDeposit ? (
              <AdornedInput.Adornment>
                <Icon size="md" css={{ color: 'icon.feedback.critical.hero' }} render={<IconWarningFilled />} />
              </AdornedInput.Adornment>
            ) : null
          }
        />
      }
      paddingBottom="sm"
    />
  );

  const renderDescription = () => {
    return (
      <Paragraph css={{ marginBottom: '400' }}>
        If you waived or reduced your security deposit when you started your{' '}
        <Text asChild>
          <Anchor href="https://www.zillow.com/z/rental-manager/rental-protection/" target="_blank">
            Rental Protection Insurance Request
          </Anchor>
        </Text>{' '}
        you may want to update it now.
      </Paragraph>
    );
  };

  const renderFields = () => {
    return securityDepositField;
  };

  const body = isLoading ? (
    <SpinnerContainer />
  ) : (
    <>
      <Box css={{ display: 'flex', flexDirection: 'column', marginBottom: '400' }}>
        <Icon
          size="xl"
          css={{ color: 'icon.accent.brand.hero', marginBlock: '200' }}
          render={<IconCheckmarkCircleFilled />}
        />
        <Heading level={4} fontFamily="heading-alt">
          Rental Protection request dropped
        </Heading>
      </Box>
      {renderDescription()}
      {renderFields()}
      <Box>
        <ExpandableDisclaimer title="Legal disclaimer" disclaimerContent={legalDisclaimer.disclaimers} />
      </Box>
    </>
  );

  return (
    <Modal
      open={true}
      onOpenChange={() => {
        handleDropRentalProtectionSecurityDepositExit();
        close();
      }}
      header={<ZillowInsuranceServicesLogo />}
      body={
        <div data-testid="SecurityDepositUpdateOnRentGuaranteeDropModal">
          <Box css={{ padding: { base: '200', md: '1200' } }}>{body}</Box>
        </div>
      }
      footer={
        <ModalFooter
          submitText={selectedOption === RENTAL_PROTECTION_PAYMENT_OPTIONS_REQUEST_VALUE.LANDLORD_PAYS ? 'OK' : 'Save'}
          onCancel={() => {
            handleDropRentalProtectionSecurityDepositCancel();
            close();
          }}
          onSubmit={handleSubmit}
        />
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

SecurityDepositUpdateOnRentGuaranteeDropModal.propTypes = propTypes;
SecurityDepositUpdateOnRentGuaranteeDropModal.defaultProps = defaultProps;

export default SecurityDepositUpdateOnRentGuaranteeDropModal;
